import axios, { InternalAxiosRequestConfig, AxiosRequestHeaders } from 'axios';
import { jwtDecode } from 'jwt-decode';
import { refreshToken as refreshTokenService } from '../services/auth.service';
import { buildAuthHeaders, buildVerificationHeaders } from '../auth/headers';
import { canAccessAdminApp } from '../auth/admin-app-access';
import { normalizeTokenPayload } from '../auth/jwt';
import { loginPathFor } from '../utils/loginRedirect';

export const axiosObject = axios.create();

function rejectPilotToken(accessToken: string): boolean {
  try {
    const payload = normalizeTokenPayload(jwtDecode<Record<string, unknown>>(accessToken));
    if (payload.is_pilot !== true) {
      return false;
    }
    void import('../stores/auth.store').then(({ syncAuthStoreFromToken }) => {
      syncAuthStoreFromToken(accessToken);
    });
    return true;
  } catch {
    return false;
  }
}

axiosObject.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    const language = sessionStorage.getItem('language') || 'en';
    let accessToken = sessionStorage.getItem('access_token');
    const refreshToken = sessionStorage.getItem('refresh_token');

    // Do not attach an Admin-forbidden pilot token while App initialization is
    // still hydrating. The store clears it and exposes the Pilot Mode message.
    if (accessToken && rejectPilotToken(accessToken)) {
      accessToken = null;
    }

    if (
      !config.url?.includes('/app_user/refresh_token') &&
      accessToken &&
      refreshToken
    ) {
      try {
        const decodedToken = jwtDecode(accessToken);
        if (decodedToken.exp) {
          const expirationTime = decodedToken.exp * 1000;

          if (Date.now() >= expirationTime) {
            const response = await refreshTokenService({ refresh_token: refreshToken });
            if (response?.access_token) {
              const decoded = jwtDecode<Record<string, unknown>>(response.access_token);
              const payload = normalizeTokenPayload(decoded);
              if (!canAccessAdminApp(payload)) {
                void import('../stores/auth.store').then(({ syncAuthStoreFromToken }) => {
                  syncAuthStoreFromToken(response.access_token);
                });
                accessToken = null;
                console.error('Refreshed token cannot access admin app');
              } else {
                accessToken = response.access_token;
                sessionStorage.setItem('access_token', response.access_token);
                sessionStorage.setItem('refresh_token', response.refresh_token);
                void import('../stores/auth.store').then(({ syncAuthStoreFromToken }) => {
                  syncAuthStoreFromToken(response.access_token);
                });
              }
            } else {
              sessionStorage.removeItem('access_token');
              sessionStorage.removeItem('refresh_token');
              console.error('Failed to refresh token');
            }
          }
        }
      } catch (error) {
        console.error('Error checking token expiration:', error);
        sessionStorage.removeItem('access_token');
        sessionStorage.removeItem('refresh_token');
      }
    }

    const isVerificationRequest = config.url?.includes('/verification/');
    const authHeaders = isVerificationRequest
      ? buildVerificationHeaders(accessToken)
      : buildAuthHeaders(accessToken);

    const updatedConfig: InternalAxiosRequestConfig = {
      ...config,
      params: {
        ...config.params,
        lang: language.toUpperCase(),
      },
      headers: {
        ...config.headers,
        ...authHeaders,
      } as AxiosRequestHeaders,
    };

    return updatedConfig;
  },
  (error) => {
    console.error('error: interceptors', error);
    return Promise.reject(error);
  },
);

axiosObject.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;

    if (status === 401) {
      sessionStorage.removeItem('access_token');
      sessionStorage.removeItem('refresh_token');
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = loginPathFor(`${window.location.pathname}${window.location.search}`);
      }
    }

    if (status === 403) {
      if (!window.location.pathname.startsWith('/login')) {
        void import('../stores/permission-denied.store').then(({ showPermissionDeniedFromError }) => {
          showPermissionDeniedFromError(error);
        });
      }
    }

    return Promise.reject(error);
  },
);

export default axiosObject;
